import { gql, useMutation, useQuery } from '@apollo/client'
import cx from 'classnames'
import Modal from 'components/Modal'
import RadioInput from 'components/RadioInput'
import TextInput from 'components/TextInput'
import { format, parse } from 'date-fns'
import { Form, Formik, FormikConfig } from 'formik'
import { Link } from 'gatsby'
import rollbar from 'helpers/rollbar'
import { formatDateOnly } from 'helpers/string'
import { isEmpty, pickBy } from 'lodash'
import { useEffect, useState } from 'react'
import { FaRedo, FaUndo } from 'react-icons/fa'
import { matchResultMutation } from './ConfirmMatchButton'
import { useToast } from './Toast'
import { ClaimResultMutation, ClaimResultMutationVariables } from './__generated__/ClaimResultMutation'
import { EscalateResultMutation, EscalateResultMutationVariables } from './__generated__/EscalateResultMutation'
import {
  GetLabEventWithPhotoQuery,
  GetLabEventWithPhotoQueryVariables,
} from './__generated__/GetLabEventWithPhotoQuery'
import { MatchResult, MatchResultVariables } from './__generated__/MatchResult'
import { UnClaimResultMutation, UnClaimResultMutationVariables } from './__generated__/UnClaimResultMutation'
import { UnmatchedResults_unmatchedResultsConnection_edges_node } from './__generated__/UnmatchedResults'

const getPhotoQuery = gql`
  query GetLabEventWithPhotoQuery($id: ID!) {
    labEventById(id: $id) {
      id
      eventType
      firstName
      lastName
      dob
      result
      photo {
        id
        filename
        mimeType
        base64
      }
    }
  }
`

const claimResultMutation = gql`
  mutation ClaimResultMutation($resultId: ID!) {
    claimResult(resultId: $resultId) {
      result {
        id
        status
        claimedAt
        claimedBy
      }
    }
  }
`

const unClaimResultMutation = gql`
  mutation UnClaimResultMutation($resultId: ID!) {
    unClaimResult(resultId: $resultId) {
      result {
        id
        status
        claimedAt
        claimedBy
      }
    }
  }
`

const escalateResultMutation = gql`
  mutation EscalateResultMutation($resultId: ID!, $versionId: ID) {
    escalateResult(resultId: $resultId, versionId: $versionId) {
      result {
        id
        status
      }
    }
  }
`

const friendlyKitId = (id) => `${id.slice(0, 4)}-${id.slice(4)}`

function validateRequired(value, ...rest) {
  if (isEmpty(value)) {
    return 'Required'
  }

  return undefined
}

const validateExactMatch = (matchString: string) => (value: string) => {
  if (value.toUpperCase() !== matchString.toUpperCase()) {
    return `Please type "${matchString}"`
  }

  return undefined
}

interface FormValues {
  firstName: string
  lastName: string
  dob: string
  result: 'NEGATIVE' | 'POSITIVE' | 'INCONCLUSIVE' | 'REJECT' | 'UNCLEAR'
  confirmResult: string
  rejectReason: string
  customRejectReason: string
}

interface ConfirmPhotoModalProps {
  result: UnmatchedResults_unmatchedResultsConnection_edges_node
  kitId: string
  sku: string
  antigenResult: string
  versionId?: string
  onClose: () => void
  readOnly?: boolean
  allowEscalate?: boolean
}

const ConfirmPhotoModal = ({
  result,
  kitId,
  sku,
  antigenResult,
  versionId,
  onClose,
  readOnly,
  allowEscalate,
}: ConfirmPhotoModalProps) => {
  const { loading, error, data } = useQuery<GetLabEventWithPhotoQuery, GetLabEventWithPhotoQueryVariables>(
    getPhotoQuery,
    {
      variables: { id: result.id },
      fetchPolicy: 'cache-and-network',
    }
  )

  const { addToast } = useToast()

  const [matchResult] = useMutation<MatchResult, MatchResultVariables>(matchResultMutation)

  const [escalateResult] = useMutation<EscalateResultMutation, EscalateResultMutationVariables>(escalateResultMutation)

  const [claimResult] = useMutation<ClaimResultMutation, ClaimResultMutationVariables>(claimResultMutation)

  const [unClaimResult] = useMutation<UnClaimResultMutation, UnClaimResultMutationVariables>(unClaimResultMutation)

  const resultId = data?.labEventById?.id

  useEffect(() => {
    if (!resultId) return

    claimResult({ variables: { resultId } }).then(() => {
      // no-op
    })

    return () =>
      unClaimResult({ variables: { resultId } }).then(() => {
        // no-op
      })
  }, [claimResult, resultId, unClaimResult])

  const [imgSize, setImgSize] = useState({ width: 0, height: 0 })
  const [rotation, setRotation] = useState(0)
  const d = imgSize.width - imgSize.height

  if (error) {
    throw error
  }

  const labEvent = data?.labEventById

  const initialValues = labEvent && {
    firstName: labEvent.firstName,
    lastName: labEvent.lastName,
    dob: formatDateOnly(labEvent.dob),
    result: labEvent.result,
    confirmResult: '',
    rejectReason: '',
    customRejectReason: '',
  }

  const friendlyId = friendlyKitId(kitId.replace('Kit-', ''))
  const resultConfirmText = (result) => `${friendlyId} ${result}`

  const onEscalate = async () => {
    try {
      await escalateResult({ variables: { resultId: result.id, versionId } })

      addToast({
        messageType: 'success',
        title: 'Escalated',
        content: <>Escalated for further review</>,
        timeout: 3000,
      })

      onClose()
    } catch (err) {
      rollbar.error('Error escalating photo result', err)
      addToast({
        messageType: 'error',
        title: 'Error',
        content: `Failed to escalate: ${err.message}`,
      })
    }
  }

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = async (values) => {
    if (values.result === 'UNCLEAR') {
      return onEscalate()
    }

    if (['NEGATIVE', 'POSITIVE', 'INCONCLUSIVE'].includes(values.result)) {
      if (values.result !== antigenResult) {
        const confirm = window.confirm(
          `The user stated the result was ${antigenResult} but you selected ${values.result}. Are you sure you want to mark this as ${values.result}?`
        )

        if (!confirm) {
          return
        }
      }
    }

    const filteredCorrections = pickBy(
      {
        firstName: values.firstName,
        lastName: values.lastName,
        dob: values.dob,
      },
      (v, k) => v !== initialValues[k]
    )
    const variables = {
      id: result.id,
      kitId,
      versionId,
      labEventCorrections:
        values.result === 'REJECT'
          ? {
              eventType: 'EXCEPTION',
              exceptionCode: values.rejectReason,
              exceptionMessage: values.customRejectReason,
            }
          : {
              result: values.result,
            },
      regCorrections: {
        ...filteredCorrections,
        ...(filteredCorrections.dob
          ? {
              dob: format(parse(filteredCorrections.dob, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd'),
            }
          : {}),
      },
    }

    try {
      await matchResult({
        variables,
      })
      addToast({
        messageType: 'success',
        title: 'Done',
        content: (
          <>
            {values.result === 'REJECT' ? 'Rejected photo submission' : 'Verified photo submission'}
            <br />
            <Link to={`/dashboard/kit/${kitId}`}>{kitId}</Link>.
          </>
        ),
        timeout: 3000,
      })

      onClose()
    } catch (err) {
      rollbar.error('Error handling photo verification', err)
      addToast({
        messageType: 'error',
        title: 'Error',
        content: `Failed to handle photo: ${err.message}`,
      })
    }
  }

  return (
    <Modal isShown={true} onRequestClose={() => onClose()}>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">
            <strong>Verify Photo &ndash; </strong>
            {labEvent ? (
              <>
                {friendlyId} ({sku})
              </>
            ) : (
              <>Loading...</>
            )}
          </p>
          <button onClick={() => onClose()} className="delete" aria-label="close"></button>
        </header>
        <section className="modal-card-body">
          {loading && <p>Loading...</p>}

          {labEvent && (
            <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit}>
              {({ values, initialValues, isSubmitting }) => (
                <Form>
                  <div className="columns">
                    <div className="column is-7">
                      <div className="mb-1 is-fullwidth is-flex">
                        <button
                          type="button"
                          className="button is-flex-grow-1 mr-1"
                          onClick={() => setRotation((prev) => prev - 1)}
                        >
                          <FaUndo />
                        </button>

                        <button
                          type="button"
                          className="button is-flex-grow-1"
                          onClick={() => setRotation((prev) => prev + 1)}
                        >
                          <FaRedo />
                        </button>
                      </div>
                      <img
                        style={{
                          transform: `rotate(${rotation * 90}deg)`,
                          marginLeft: (-d / 2) * (rotation % 2),
                          marginTop: (d / 2) * (rotation % 2),
                        }}
                        onLoad={(e) => {
                          setImgSize({
                            width: e.target.offsetWidth,
                            height: e.target.offsetHeight,
                          })
                        }}
                        src={labEvent.photo.base64}
                        alt="Passport and test cassette"
                      />
                    </div>

                    {!readOnly && (
                      <div className="column is-5">
                        <TextInput
                          name="firstName"
                          className="is-family-monospace"
                          label={`First Name (registered: "${initialValues.firstName}")`}
                          validate={validateRequired}
                        />

                        <TextInput
                          name="lastName"
                          className="is-family-monospace"
                          label={`Last Name (registered: "${initialValues.lastName}")`}
                          validate={validateRequired}
                        />

                        <TextInput
                          name="dob"
                          className="is-family-monospace"
                          label={`DOB (registered: "${initialValues.dob}"). DD/MM/YYYY`}
                          validate={validateRequired}
                        />

                        <RadioInput
                          name="result"
                          validate={validateRequired}
                          labelClassName="is-size-7 has-text-weight-light"
                          options={[
                            { value: 'NEGATIVE', label: 'Negative' },
                            { value: 'POSITIVE', label: 'Positive' },
                            { value: 'INCONCLUSIVE', label: 'Inconclusive' },
                            ...(allowEscalate
                              ? [
                                  {
                                    value: 'UNCLEAR',
                                    label: 'Escalate',
                                  },
                                ]
                              : []),
                            {
                              value: 'REJECT',
                              label: <span className="has-text-danger">Reject</span>,
                            },
                          ]}
                        >
                          Result
                        </RadioInput>

                        {values.result === 'REJECT' && (
                          <RadioInput
                            name="rejectReason"
                            validate={validateRequired}
                            listLayout
                            itemLabelClassName="mb-2"
                            labelClassName="is-size-7 has-text-weight-light"
                            options={[
                              {
                                value: 'PHOTO',
                                label: 'Test cassette and/or ID document not clearly visible',
                              },
                              {
                                value: 'ID',
                                label: 'Kit ID on cassette does not match registered ID',
                              },
                              {
                                value: 'DETAILS',
                                label: 'Registered details do not match ID document',
                              },
                              {
                                value: 'CASSETTE',
                                label: 'Test cassette is not one of our brands',
                              },
                              {
                                value: 'CUSTOM',
                                label: 'Custom reason',
                              },
                            ]}
                          >
                            Reject reason
                          </RadioInput>
                        )}

                        {values.result === 'REJECT' && values.rejectReason === 'CUSTOM' && (
                          <TextInput
                            name="customRejectReason"
                            component="textarea"
                            className="textarea"
                            validate={validateRequired}
                            label="Message to customer"
                          />
                        )}
                        <hr />

                        {['NEGATIVE', 'POSITIVE', 'INCONCLUSIVE'].includes(values.result) && (
                          <TextInput
                            name="confirmResult"
                            className="has-placeholder-darker"
                            label={<>Type the kit ID followed by '{values.result}' to confirm</>}
                            placeholder={resultConfirmText(values.result)}
                            validate={validateExactMatch(resultConfirmText(values.result))}
                          />
                        )}

                        <button
                          type="submit"
                          className={cx(
                            'button is-primary is-outlined is-fullwidth mb-3',
                            {
                              'is-loading': isSubmitting,
                            },
                            values.result.startsWith('REJECT') ? 'is-danger' : 'is-primary'
                          )}
                          disabled={isSubmitting}
                        >
                          {values.result === 'REJECT' && 'Reject photo'}

                          {values.result === 'UNCLEAR' && 'Escalate for further review'}

                          {['NEGATIVE', 'POSITIVE', 'INCONCLUSIVE'].includes(values.result) && (
                            <>Confirm {values.result} result</>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </section>
      </div>
    </Modal>
  )
}

export default ConfirmPhotoModal
